<template>
  <div class="navbar-root">
    <div class="navbar-container" :class="{ collapsed: isCollapsed }">
      <!-- Logo区域 -->
      <div class="navbar-logo" @click="toggleCollapse">
        <div class="logo-content">
          <div class="logo-icon">
            <i class="el-icon-s-shop"></i>
            <span v-if="!isCollapsed" class="logo-text">企业AI系统</span>
          </div>
        </div>
        <div class="collapse-btn" @click.stop="toggleCollapse">
          <i :class="isCollapsed ? 'el-icon-s-unfold' : 'el-icon-s-fold'"></i>
        </div>
      </div>

      <!-- 菜单区域 -->
      <div class="menu-wrapper">
        <div class="navbar-menu" ref="navbarMenu" v-if="!loading">
          <div
            v-for="(item, index) in getListData"
            :key="index"
            class="menu-item"
            :class="{ 
              active: activeMenu === index + '', 
              'has-submenu': item.children && item.children.length > 0 
            }"
            :data-index="index"
            @click="handleMenuSelect(index + '')"
            @mouseenter="handleMenuHover(item)"
          >
            <div class="menu-icon">
              <i :class="item.icon || 'el-icon-menu'"></i>
            </div>
            <div class="menu-text" v-if="!isCollapsed">
              {{ item.name || '未命名菜单' }}
            </div>
            <div class="menu-arrow" v-if="!isCollapsed && item.children && item.children.length > 0">
              <i class="el-icon-arrow-right"></i>
            </div>
          </div>
          <div v-if="getListData.length === 0" class="empty-menu">暂无菜单权限</div>
        </div>
        <div class="menu-loading" v-else>
          <i class="el-icon-loading"></i> 菜单加载中...
        </div>
      </div>
    </div>

    <!-- 弹出式子菜单 -->
    <transition name="submenu-fade">
      <div
        v-if="showSubmenu && activeSubmenu"
        class="submenu-panel"
        :class="{ collapsed: isCollapsed }"
        :style="{ top: submenuTop + 'px' }"
        @mouseenter="handleSubmenuEnter"
        @mouseleave="handleSubmenuLeave"
        ref="submenuPanel"
      >
        <div class="submenu-header">
          <div class="header-icon">
            <i class="el-icon-menu"></i>
          </div>
          <h3>{{ activeSubmenu.name || '子菜单' }}</h3>
          <div class="submenu-close" @click="closeSubmenu">
            <i class="el-icon-close"></i>
          </div>
        </div>

        <div class="submenu-content">
          <div class="submenu-list">
            <template v-for="(subItem, subIndex) in activeSubmenu.children">
              <!-- 有三级菜单 -->
              <div
                v-if="subItem.children && subItem.children.length > 0"
                :key="'sub-' + subIndex"
                class="second-menu-item has-children"
                @mouseenter="() => handleSecondMenuHover(subItem, subIndex)"
                @mouseleave="handleSecondMenuLeave"
              >
                <div class="second-menu-title" @click.stop="handleSecondMenuClick(subItem)">
                  <i :class="subItem.icon || 'el-icon-menu'"></i>
                  <span>{{ subItem.name || '未命名子菜单' }}</span>
                  <i class="el-icon-arrow-right arrow-icon"></i>
                </div>

                <transition name="third-menu-fade">
                  <div
                    v-show="activeThirdMenu === subIndex + ''"
                    class="third-menu-panel"
                  >
                    <div
                      v-for="(thirdItem, thirdIndex) in subItem.children"
                      :key="'third-' + thirdIndex"
                      class="third-menu-item"
                      @click.stop="handleThirdMenuClick(thirdItem)"
                    >
                      <i :class="thirdItem.icon || 'el-icon-menu'"></i>
                      <span>{{ thirdItem.name || '未命名三级菜单' }}</span>
                    </div>
                  </div>
                </transition>
              </div>

              <!-- 只有二级菜单 -->
              <div
                v-else
                :key="'sub-normal-' + subIndex"
                class="second-menu-item"
                @click.stop="handleSecondMenuClick(subItem)"
              >
                <i :class="subItem.icon || 'el-icon-menu'"></i>
                <span>{{ subItem.name || '未命名子菜单' }}</span>
              </div>
            </template>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "NavbarView",
  data() {
    return {
      isCollapsed: false,
      activeMenu: '',
      activeSubmenu: null,
      activeThirdMenu: null,
      showSubmenu: false,
      submenuTop: 0,
      getListData: [],
      hoverTimer: null,
      leaveTimer: null,
      clickMode: false,
      loading: true
    }
  },
  watch: {
    isCollapsed(newVal) {
      if (newVal) this.closeSubmenu();
      this.$emit('collapse-change', newVal);
      localStorage.setItem('menuCollapsed', newVal);
      if (this.showSubmenu && this.activeSubmenu) {
        this.$nextTick(() => this.calculateSubmenuPosition());
      }
    }
  },
  created() {
    const savedCollapsed = localStorage.getItem('menuCollapsed');
    this.isCollapsed = savedCollapsed !== null ? JSON.parse(savedCollapsed) : false;
    this.menuData();
  },
  mounted() {
    this.setDefaultActiveMenu();
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeDestroy() {
    if (this.hoverTimer) clearTimeout(this.hoverTimer);
    if (this.leaveTimer) clearTimeout(this.leaveTimer);
    document.removeEventListener('click', this.handleDocumentClick);
  },
  methods: {
    async menuData() {
      this.loading = true;
      try {
        const roles = JSON.parse(sessionStorage.getItem('roles') || '[]');
        const param = {
          company_id: sessionStorage.getItem('company_id') || '',
          access_token: sessionStorage.getItem('token') || '',
          uid: sessionStorage.getItem('uid') || '',
          role_id: roles.map(role => role.id).filter(Boolean).join(',')
        };
        const res = await this.$axios.proxyModuleList(param);
        if (res.code === 200) {
          this.getListData = res.data || [];
          if (this.getListData.length > 0 && !this.activeMenu) {
            this.activeMenu = '0';
          }
        }
      } catch (error) {
        console.error('获取菜单数据失败:', error);
        this.getListData = [];
      } finally {
        this.loading = false;
      }
    },

    handleMenuSelect(index) {
      if (this.loading || this.getListData.length === 0) return;
      this.clickMode = true;
      if (this.hoverTimer) clearTimeout(this.hoverTimer);

      const menuItem = this.getListData[Number(index)];
      if (menuItem) {
        if (menuItem.children && menuItem.children.length > 0) {
          this.activeMenu = index;
          this.activeSubmenu = menuItem;
          this.showSubmenu = true;
          this.$nextTick(() => this.calculateSubmenuPosition());
        } else if (menuItem.path) {
          const path = menuItem.path.startsWith('/') ? menuItem.path : '/' + menuItem.path;
          this.$router.push(path).catch(err => console.error('路由跳转失败:', err));
          this.closeSubmenu();
        } else {
          console.warn('菜单项没有path且没有子菜单:', menuItem);
        }
      }
      setTimeout(() => (this.clickMode = false), 100);
    },

    handleMenuHover(item) {
      if (this.clickMode || this.isCollapsed || this.loading) return;
      if (this.leaveTimer) clearTimeout(this.leaveTimer);
      if (this.hoverTimer) clearTimeout(this.hoverTimer);

      this.hoverTimer = setTimeout(() => {
        if (item && item.children && item.children.length > 0) {
          const index = this.getListData.findIndex(i => i === item);
          this.activeMenu = index + '';
          this.activeSubmenu = item;
          this.showSubmenu = true;
          this.$nextTick(() => this.calculateSubmenuPosition());
        }
      }, 200);
    },

    calculateSubmenuPosition() {
      const activeElement = document.querySelector('.menu-item.active');
      if (!activeElement || !this.$el) return;

      const rect = activeElement.getBoundingClientRect();
      const navbarRect = this.$el.getBoundingClientRect();
      let top = rect.top - navbarRect.top - 10;

      const submenuHeight = this.activeSubmenu?.children ? this.activeSubmenu.children.length * 48 + 70 : 350;
      const windowHeight = window.innerHeight;

      if (top + submenuHeight > windowHeight && windowHeight > submenuHeight + 20) {
        top = windowHeight - submenuHeight - 20;
      }
      this.submenuTop = Math.max(10, Math.min(top, windowHeight - submenuHeight - 10));
    },

    handleSubmenuEnter() {
      if (this.leaveTimer) clearTimeout(this.leaveTimer);
    },

    handleSubmenuLeave() {
      if (this.isCollapsed) return;
      if (this.leaveTimer) clearTimeout(this.leaveTimer);

      this.leaveTimer = setTimeout(() => {
        const isHover = document.querySelector('.submenu-panel:hover, .second-menu-item:hover, .third-menu-panel:hover');
        if (!isHover) this.closeSubmenu();
      }, 300);
    },

    handleSecondMenuHover(subItem, subIndex) {
      if (subItem && subItem.children && subItem.children.length > 0) {
        this.activeThirdMenu = subIndex + '';
      }
    },

    handleSecondMenuLeave() {
      setTimeout(() => {
        const isHover = document.querySelector('.third-menu-panel:hover, .second-menu-item.has-children:hover');
        if (!isHover) this.activeThirdMenu = null;
      }, 100);
    },

    handleSecondMenuClick(subItem) {
      if (!subItem) return;
      if (!subItem.children || subItem.children.length === 0) {
        if (subItem.path && subItem.path !== '#') {
          const path = subItem.path.startsWith('/') ? subItem.path : '/' + subItem.path;
          this.$router.push(path).catch(err => console.error('路由跳转失败:', err));
          this.closeSubmenu();
        } else {
          this.$message.warning('该菜单暂无跳转链接');
        }
      }
    },

    handleThirdMenuClick(thirdItem) {
      if (!thirdItem || !thirdItem.path || thirdItem.path === '#') {
        this.$message.warning('该菜单暂无跳转链接');
        return;
      }
      const path = thirdItem.path.startsWith('/') ? thirdItem.path : '/' + thirdItem.path;
      this.$router.push(path).catch(err => console.error('路由跳转失败:', err));
      this.closeSubmenu();
    },

    closeSubmenu() {
      this.showSubmenu = false;
      this.activeThirdMenu = null;
      if (this.hoverTimer) clearTimeout(this.hoverTimer);
      if (this.leaveTimer) clearTimeout(this.leaveTimer);
    },

    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
      this.$emit('collapse-change', this.isCollapsed);
    },

    setDefaultActiveMenu() {
      const currentPath = this.$route.path;
      const findActiveMenu = (menuList) => {
        for (let i = 0; i < menuList.length; i++) {
          const item = menuList[i];
          if (item.path && currentPath === '/' + item.path) return i + '';
          if (item.children && item.children.length > 0) {
            const childId = findActiveMenu(item.children);
            if (childId) return childId;
          }
        }
        return '';
      };
      const activeId = findActiveMenu(this.getListData);
      if (activeId) this.activeMenu = activeId;
    },

    handleDocumentClick(event) {
      const navbar = this.$el;
      const submenuPanel = this.$refs.submenuPanel;
      if (!navbar) return;
      if (submenuPanel && submenuPanel.contains(event.target)) return;
      if (!navbar.contains(event.target)) this.closeSubmenu();
    }
  }
}
</script>

<style lang="scss" scoped>
.navbar-root {
  height: 100%;
  width: 100%;
}

.navbar-container {
  width: 220px;
  height: 100%;
  background: linear-gradient(180deg, #1a2332 0%, #15202b 100%);
  position: relative;
  display: flex;
  flex-direction: column;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  box-shadow: 2px 0 12px rgba(0, 0, 0, 0.08);

  &.collapsed {
    width: 72px !important;

    .navbar-menu {
      .menu-text,
      .menu-arrow {
        opacity: 0;
        width: 0;
        margin: 0;
      }
    }

    .navbar-logo {
      justify-content: center;
      padding: 0 12px;
    }

    .collapse-btn i {
      transform: rotate(180deg);
    }
  }
}

.menu-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Logo区域 */
.navbar-logo {
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  background: rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
}

.logo-content {
  display: flex;
  align-items: center;
}

.logo-icon {
  display: flex;
  align-items: center;
  gap: 10px;

  i {
    font-size: 28px;
    color: #1677ff;
    background: #fff;
    padding: 6px;
    border-radius: 12px;
  }

  .logo-text {
    color: #fff;
    font-size: 18px;
    font-weight: 600;
    white-space: nowrap;
    transition: all 0.3s;
    letter-spacing: 1px;
  }
}

.collapse-btn {
  color: rgba(255, 255, 255, 0.6);
  font-size: 18px;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  transition: all 0.3s;

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.1);
  }

  i {
    transition: transform 0.3s;
  }
}

/* 菜单区域 */
.navbar-menu {
  flex: 1;
  overflow-y: auto;
  padding: 12px 0;
  scrollbar-width: thin;
  scrollbar-color: #4a5568 transparent;
}

.navbar-menu::-webkit-scrollbar {
  width: 4px;
}

.navbar-menu::-webkit-scrollbar-track {
  background: transparent;
}

.navbar-menu::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}

.menu-item {
  height: 48px;
  display: flex;
  align-items: center;
  padding: 0 20px;
  margin: 4px 12px;
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: all 0.3s;
  position: relative;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
  }

  &.active {
    background: linear-gradient(135deg, #1677ff, #4096ff);
    color: #fff;
    box-shadow: 0 2px 8px rgba(22, 119, 255, 0.3);

    .menu-icon i {
      color: #fff;
    }

    .menu-arrow {
      color: #fff;
    }
  }
}

.menu-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;

  i {
    transition: all 0.3s;
  }
}

.menu-text {
  flex: 1;
  margin-left: 12px;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  opacity: 1;
  transition: all 0.3s;
  text-align: left;
}

.menu-arrow {
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  transition: all 0.3s;
  margin-left: 8px;
}

.empty-menu,
.menu-loading {
  text-align: center;
  padding: 40px 20px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
}

.menu-loading i {
  margin-right: 6px;
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 子菜单面板 */
.submenu-panel {
  position: fixed;
  left: 220px;
  top: 0;
  width: 260px;
  background: #fff;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.15);
  border-radius: 12px;
  overflow: hidden;
  z-index: 1001;

  &.collapsed {
    left: 72px !important;
  }
}

.submenu-fade-enter-active,
.submenu-fade-leave-active {
  transition: all 0.25s ease;
}

.submenu-fade-enter-from,
.submenu-fade-leave-to {
  transform: translateX(-8px);
  opacity: 0;
}

.submenu-header {
  height: 52px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  background: linear-gradient(135deg, #1677ff, #4096ff);
  gap: 10px;

  .header-icon {
    width: 28px;
    height: 28px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;

    i {
      color: #fff;
      font-size: 16px;
    }
  }

  h3 {
    flex: 1;
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.submenu-close {
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  font-size: 16px;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s;

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.15);
  }
}

.submenu-content {
  flex: 1;
  overflow-y: auto;
  max-height: calc(80vh - 52px);
  padding: 8px 0;
}

.submenu-list {
  padding: 0;
}

.second-menu-item {
  padding: 0 16px;
  height: 44px;
  display: flex;
  align-items: center;
  color: #1f2d3d;
  cursor: pointer;
  transition: all 0.25s;
  margin: 2px 8px;
  border-radius: 8px;

  &:hover {
    background: #f0f7ff;
    color: #1677ff;

    i:first-child {
      color: #1677ff;
    }
  }

  i:first-child {
    margin-right: 10px;
    font-size: 16px;
    color: #8c8f98;
    transition: all 0.25s;
  }

  span {
    flex: 1;
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.second-menu-item.has-children {
  position: relative;

  .second-menu-title {
    display: flex;
    align-items: center;
    width: 100%;

    .arrow-icon {
      margin-left: auto;
      font-size: 12px;
      color: #c0c4cc;
      transition: all 0.25s;
    }

    &:hover .arrow-icon {
      color: #1677ff;
    }
  }
}

.third-menu-panel {
  position: absolute;
  left: calc(100% - 8px);
  top: 0;
  width: 220px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  overflow: hidden;
  z-index: 1002;
  padding: 8px 0;
}

.third-menu-fade-enter-active,
.third-menu-fade-leave-active {
  transition: all 0.2s ease;
}

.third-menu-fade-enter-from,
.third-menu-fade-leave-to {
  transform: translateX(-8px);
  opacity: 0;
}

.third-menu-item {
  padding: 0 16px;
  height: 40px;
  display: flex;
  align-items: center;
  color: #606266;
  cursor: pointer;
  transition: all 0.25s;
  margin: 2px 8px;
  border-radius: 8px;

  &:hover {
    background: #f0f7ff;
    color: #1677ff;
  }

  i {
    margin-right: 10px;
    font-size: 14px;
  }

  span {
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

/* 滚动条样式 */
.submenu-content::-webkit-scrollbar,
.third-menu-panel::-webkit-scrollbar {
  width: 4px;
}

.submenu-content::-webkit-scrollbar-track,
.third-menu-panel::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.submenu-content::-webkit-scrollbar-thumb,
.third-menu-panel::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.submenu-content::-webkit-scrollbar-thumb:hover,
.third-menu-panel::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 响应式 */
@media screen and (max-width: 992px) {
  .navbar-container {
    position: fixed;
    left: 0;
    top: 0;
    bottom: 0;
    z-index: 1000;
    transform: translateX(0);
    transition: transform 0.3s;

    &.collapsed {
      transform: translateX(-100%);
    }
  }

  .submenu-panel {
    position: fixed;
    left: 220px;
    top: 0;
    z-index: 1001;
  }
}
</style>